/**
 * usageTracker — Yango Report App client-side telemetry sink
 * (AB#4277 / FE-1, Yango usage-tracking workstream 2026-06-10).
 *
 * Ported from the Libertas reference implementation
 * (`SmartLodging/FrontEnd/src/services/usageTracker.js`, ~100 LOC,
 * production-grade, 30s flush + 5min backoff cap). The tracker batches
 * page-view, form-submit, click, login, logout, verify, and export
 * events into an in-memory queue and ships them to the SynergyApi
 * `POST /api/v1/usage/record/batch` endpoint. See
 * `docs/design-docs/2026-06-10-yango-usage-tracking-PLAN.md` §3 FE-1
 * and `docs/design-docs/2026-06-10-yango-usage-tracking-SPEC.md` for
 * the full contract. The full hand-off rationale for every design
 * decision below lives in
 * `docs/reviews/2026-06-10-AB#4260-usage-tracker-ts-passA.md`
 * (findings F-1..F-9).
 *
 * ─── Source-of-truth constants (verified 2026-06-10) ────────────────
 *
 * `APP_NAME` ("yango-reports-app") is the SynergyApi app id 72,
 * runtime_type WEB_APP, owner Christo Naude. Verified via the public
 * `GET https://lna.pos.co.zm/SynergyApi/api/v1/usage/apps` endpoint
 * (no auth required). The FE-side `app_name` is intentionally a hard
 * constant — there is no per-environment override. The corresponding
 * BE-side `app_name` is `"yango-smart-invoice-api-middleware"`
 * (SynergyApi id 73, INTEGRATION, owner Christo Naude). They are
 * separate apps, deliberately registered separately.
 *
 * `API_BASE` is the FE-side SynergyApi usage root. The batch endpoint
 * is `POST {API_BASE}/record/batch`. CORS pre-flight from
 * `yango.abc.co.zm` to `lna.pos.co.zm` MUST succeed for any event to
 * be delivered — see AR-1 in the Pass A review.
 *
 * ─── Closed-enum `Action` (F-2) ─────────────────────────────────────
 *
 * `Action` is a closed string-literal union of the seven telemetry
 * actions the FE is allowed to emit. The strict-mode TypeScript type
 * prevents the compile-time contract from drifting, but at runtime the
 * field arrives as a string — a `.vue` SFC could call
 * `usageTracker.track('page:foo', { action: 'BAD' as any })`. The
 * runtime guard in `track()` (the `ACTIONS` tuple membership check)
 * drops the event with a `console.warn` and does NOT corrupt the
 * queue. This mirrors the Libertas runtime-noop pattern and is the
 * canonical enforcement of the Carlos-Mendez C1.3 / C4.3 contract
 * (closed-enum).
 *
 *   type Action = 'view' | 'submit' | 'click' | 'login' | 'logout' | 'verify' | 'export'
 *
 * `'tick'` is BE-only (the `UsageTrackerService` schedules a 30-min
 * flush — see AB#4265). `'otp-verified'` was folded into `'verify'`
 * for FE consistency (the OTP form emits `'verify'`, see
 * `views/Otp.vue:86`). The 7 members above are the final set; the
 * tuple `ACTIONS` is the runtime source of truth and is iterated
 * only for diagnostic logging.
 *
 * ─── `getUserId()` resolution order (F-3, Yango-specific) ───────────
 *
 *   1. `'authenticated'` sentinel when `sessionStorage['yango-session-active']`
 *      is set (Q-17 cookie model). The opaque UUID lives in an httpOnly
 *      cookie the SPA cannot read; telemetry uses this sentinel instead.
 *   2. `(window as Window & { userId?: string }).userId` — set in
 *      the legacy JSP shim that bootstraps the SPA. Used as a
 *      fallback for the brief window after page load but before OTP
 *      verify completes. NEVER trust this directly: cap at 64 chars
 *      and strip control chars to defend against a malicious browser
 *      extension or compromised XHR interceptor that sets it to
 *      arbitrary content.
 *   3. `'anonymous'` — the cold-start funnel identifier. Used before
 *      OTP verify. Deliberate: anonymous events are valuable signal
 *      (cold-start conversion), not bugs. Diana Reyes confirmed
 *      'anonymous' appears as a distinct user cohort in the dashboard.
 *
 * This is INTENTIONALLY different from Libertas, which reads
 * `localStorage.getItem('user').id`. Yango has no `user` localStorage
 * key. The divergence is documented in the gap analysis §2.2 and
 * `MEMORY.md`.
 *
 * ─── Timestamp discipline (F-1, audit-trail integrity) ─────────────
 *
 * Every event's `timestamp` is captured ONCE at enqueue time as
 * `new Date().toISOString()` — RFC 3339 UTC with the `Z` suffix. The
 * timestamp is stored in the queue and shipped verbatim at flush
 * time; it is NEVER regenerated. This is the canonical telemetry
 * pattern: the audit trail records WHEN the event happened, not WHEN
 * it was uploaded. A typical gap of 0..30 seconds between enqueue
 * and flush is expected and not a bug.
 *
 * ─── Backoff re-set on flush failure (F-4) ──────────────────────────
 *
 * On a flush failure (network error, 5xx, 429), the current
 * interval is torn down with `clearInterval` and a NEW interval is
 * registered at the doubled delay (capped at 5 minutes). Without
 * the `clearInterval` call, the OLD interval would keep firing at
 * the OLD delay while the new (longer) delay only took effect for
 * the NEXT scheduled tick — a subtle bug in the Libertas reference
 * that this port fixes. On flush success, the delay is reset to
 * the 30-second base.
 *
 * ─── `beforeunload` best-effort semantics (R-4) ────────────────────
 *
 * The `beforeunload` listener performs a final flush attempt when
 * the user navigates away. This is BEST-EFFORT, not reliable:
 *   - Modern browsers do not block unload on fetch promises, so the
 *     in-flight POST may be cancelled mid-flight.
 *   - Mobile Safari and some Android browsers kill the page
 *     immediately, with no `beforeunload` callback at all.
 *   - The events in the final queue are LOST in those scenarios.
 *
 * We accept this reliability gap because the alternative (a
 * `sendBeacon` upgrade) is deferred per the gap analysis. Do not
 * advertise this as a guarantee; do not surface this as a count
 * mismatch bug. The 30-second flush interval is the primary
 * delivery mechanism.
 *
 * ─── Module-level state (F-5) ──────────────────────────────────────
 *
 * The `queue`, `flushIntervalId`, `currentRetryDelay`, and
 * `initialised` bindings are all module-private. `init()` is guarded
 * by `initialised` — calling it twice (HMR, tests) is a no-op the
 * second time. The tracker is fire-and-forget: every public method
 * returns `void` and never throws.
 */

// ─── Source-of-truth constants (verified 2026-06-10) ─────────────────

/**
 * SynergyApi `app_name` for the Yango Report App frontend. SynergyApi
 * id 72, runtime_type WEB_APP, owner Christo Naude. Hard-coded — no
 * per-environment override. Verified against
 * `GET https://lna.pos.co.zm/SynergyApi/api/v1/usage/apps` 2026-06-10.
 */
const APP_NAME = 'yango-reports-app';

/**
 * SynergyApi usage-events root. The batch endpoint is
 * `POST {API_BASE}/record/batch` and accepts a JSON array of
 * `UsageEvent` payloads (the same shape this module enqueues).
 */
const API_BASE = 'https://lna.pos.co.zm/SynergyApi/api/v1/usage';

/**
 * Base flush cadence (ms) on the happy path. 30 seconds mirrors the
 * Libertas reference. Configurable in the future via env var; the
 * gap analysis recommends a kill switch (`VITE_USAGE_TRACKING_ENABLED`)
 * which is honoured at the top of `init()`.
 */
const BASE_FLUSH_INTERVAL = 30000;

/**
 * Hard cap on the exponential backoff delay (ms). 5 minutes. After
 * the cap is reached, every subsequent failure keeps the delay at
 * `MAX_RETRY_DELAY`; the cap is not broken through.
 */
const MAX_RETRY_DELAY = 300000;

/**
 * Maximum in-memory queue size. When the queue is full, the
 * OLDEST event is dropped (FIFO eviction) to make room for the new
 * one. 500 events is roughly 16 minutes of page-view traffic at
 * the typical 1 event / 2 seconds rate — enough headroom to ride
 * out a 15-minute SynergyApi outage.
 */
const MAX_QUEUE_SIZE = 500;

/**
 * Hard cap on the `user_id` string length after resolution. 64
 * characters is generous for the Yango `session-id` token (typically
 * 32..48 chars of base64) and the `window.userId` legacy shim value,
 * while preventing a malicious extension from injecting megabytes of
 * garbage into every batch.
 */
const USER_ID_MAX_LENGTH = 64;

// ─── Closed-enum `Action` (F-2) ─────────────────────────────────────

/**
 * Closed string-literal union of the seven telemetry actions the
 * FE is allowed to emit. The TypeScript type prevents compile-time
 * drift; the runtime guard in `track()` (the `ACTIONS` membership
 * check) prevents runtime drift. See file header for the rationale
 * and the canonical set of values.
 */
type Action = 'view' | 'submit' | 'click' | 'login' | 'logout' | 'verify' | 'export';

/**
 * Runtime source-of-truth for the `Action` closed enum. Iterated
 * only for diagnostic logging — the membership check in `track()`
 * uses `ACTIONS.indexOf` for O(n) lookup (n=7, so the constant
 * factor is irrelevant). Kept in lock-step with the `Action` type:
 * adding a new action requires both a new union member AND a new
 * tuple entry.
 */
const ACTIONS: readonly Action[] = [
  'view',
  'submit',
  'click',
  'login',
  'logout',
  'verify',
  'export',
] as const;

/**
 * Caller-supplied options for `track()`. `action` is required: a
 * missing action is a contract violation (the closed-enum guard
 * drops the event in that case too, not just on bad values).
 */
interface TrackOptions {
  action: Action;
  metadata?: Record<string, unknown>;
}

/**
 * One telemetry event. The shape mirrors the SynergyApi
 * `UsageEvent` record verbatim so the same JSON can be uploaded
 * unchanged. `user_id`, `timestamp`, and `app_name` are filled in
 * by the tracker at enqueue time — callers do not supply them.
 */
interface UsageEvent {
  app_name: string;
  user_id: string;
  feature: string;
  timestamp: string;
  metadata: {
    action: Action;
    [key: string]: unknown;
  };
}

// ─── Module-private state (F-5) ─────────────────────────────────────

/**
 * In-memory event queue. The single source of truth for "events
 * the tracker has accepted but not yet shipped". On a successful
 * flush, the entire batch is spliced out. On a failure, the batch
 * is prepended back so retry ordering is preserved. Module-private;
 * only `enqueue`, `flush`, and the FIFO-eviction logic in
 * `enqueue` touch it.
 */
let queue: UsageEvent[] = [];

/**
 * `setInterval` handle for the periodic flush. `null` before
 * `init()` and after the module is torn down (HMR only). Re-set on
 * every backoff transition (F-4): the OLD interval is cleared and
 * a NEW interval is registered at the new delay. Without the
 * re-set, the old delay would keep firing until the new tick.
 */
let flushIntervalId: ReturnType<typeof setInterval> | null = null;

/**
 * Current backoff delay in ms. Starts at `BASE_FLUSH_INTERVAL`,
 * doubles on each failure, capped at `MAX_RETRY_DELAY`, reset to
 * `BASE_FLUSH_INTERVAL` on the first success. Tracked separately
 * from `flushIntervalId` so the reschedule is a single arithmetic
 * step.
 */
let currentRetryDelay = BASE_FLUSH_INTERVAL;

/**
 * Double-init guard. `init()` is idempotent: a second call (HMR,
 * repeated test setup) is a silent no-op. The guard is
 * module-level, not tied to any specific instance, because the
 * tracker itself is a singleton (one queue, one interval).
 */
let initialised = false;

// ─── PII hygiene (F-3) ──────────────────────────────────────────────

/**
 * Resolve the current user identifier using the Yango-specific
 * fallback chain. See file header for the full rationale.
 *
 * Order:
 *   1. `'authenticated'` when `yango-session-active` flag is set (Q-17).
 *   2. `(window as Window & { userId?: string }).userId` — legacy
 *      JSP-shim fallback for the cold-start window.
 *   3. `'anonymous'` — deliberate cold-start funnel identifier.
 *
 * The returned value is always length-capped and control-char
 * stripped (F-3) before being placed in an event payload. The
 * function never throws and never returns `null` / `undefined`.
 *
 * @returns A non-empty, length-bounded, control-char-free user
 *   identifier suitable for direct inclusion in a `UsageEvent`.
 */
function getUserId(): string {
  let sessionActive = false;
  try {
    sessionActive = sessionStorage.getItem('yango-session-active') === 'true';
  } catch {
    sessionActive = false;
  }
  const winUserId = (window as Window & { userId?: string }).userId;
  const raw = sessionActive ? 'authenticated' : (winUserId || 'anonymous');
  // F-3: length cap (USER_ID_MAX_LENGTH) and control-char strip.
  // `[\x00-\x1F\x7F]` matches ASCII control chars including DEL.
  return String(raw).slice(0, USER_ID_MAX_LENGTH).replace(/[\x00-\x1F\x7F]/g, '');
}

/**
 * Strip ASCII control characters from a feature string. Feature
 * strings are static literals in the current callers (`'page:' +
 * to.name`, `'form:otp'`) but the strip is applied defensively so
 * a future caller that interpolates a route parameter cannot
 * inject CRLF or other control sequences into a batch payload.
 * The strip is the same regex as `getUserId()`; we keep the
 * function split out so the two concerns are independently
 * testable.
 *
 * @param feature The feature string to sanitise.
 * @returns The feature string with control chars removed.
 */
function sanitiseFeature(feature: string): string {
  return String(feature).replace(/[\x00-\x1F\x7F]/g, '');
}

// ─── Public API ─────────────────────────────────────────────────────

/**
 * Enqueue a telemetry event for later shipping. The event is
 * captured at enqueue time — `timestamp` is set once and never
 * regenerated (F-1). The action is validated against the closed
 * enum (F-2): a missing or unknown action causes the event to
 * be dropped with a `console.warn`; the queue is NOT corrupted.
 *
 * Callers should use the form
 *   `usageTracker.track('page:' + name, { action: 'view' })`
 * for page views, and
 *   `usageTracker.track('form:login', { action: 'login' })`
 * for form submits. The `metadata` object is merged into the
 * event's `metadata` field verbatim, so callers can attach
 * arbitrary structured data (form field count, error code, etc.).
 *
 * Fire-and-forget: this method never throws and never returns
 * a meaningful value. It is safe to call from a Vue `afterEach`
 * hook, a `submit` handler, or a `click` listener without
 * wrapping in try/catch.
 *
 * @param feature The feature identifier, conventionally
 *   `'<namespace>:<name>'` (e.g. `'page:InvoiceStatus'`,
 *   `'form:otp'`, `'action:export'`). Static literal — never
 *   interpolate user input.
 * @param options Call-site options. `action` is required and
 *   must be one of the seven closed-enum values. `metadata` is
 *   optional and is merged into the event's `metadata` field.
 */
function track(feature: string, options: TrackOptions): void {
  // F-2: runtime closed-enum guard. Compile-time is enforced by
  // the `Action` type; runtime is enforced here. A missing or
  // unknown action is a contract violation and the event is
  // dropped without touching the queue.
  if (!options || typeof options.action !== 'string' || ACTIONS.indexOf(options.action) === -1) {
    console.warn('[usageTracker] dropped event with invalid action:', options);
    return;
  }
  // F-1: timestamp captured ONCE at enqueue time, never
  // regenerated. RFC 3339 UTC with the Z suffix (C1.1).
  const event: UsageEvent = {
    app_name: APP_NAME,
    user_id: getUserId(),
    feature: sanitiseFeature(feature),
    timestamp: new Date().toISOString(),
    metadata: {
      ...(options.metadata || {}),
      action: options.action,
    },
  };
  // FIFO eviction when the queue is full: drop the OLDEST event
  // to make room. This is preferable to dropping the NEW event
  // (which would lose the most-recent user action) and preferable
  // to refusing the new event (which would surface as a caller
  // bug). 500 events is roughly 16 minutes of page-view traffic
  // at the typical 1 event / 2 seconds rate.
  if (queue.length >= MAX_QUEUE_SIZE) {
    queue.shift();
  }
  queue.push(event);
}

/**
 * Convenience wrapper for page-view events. Equivalent to
 *   `track('page:' + name, { action: 'view' })`
 * but reads more naturally at the call site. Used by
 * `router/index.ts` (the `afterEach` hook) and intended for
 * future callers that need a page-view event outside the
 * router context (e.g., a modal that counts as a distinct
 * "page" in the funnel).
 *
 * No dedup is performed (F-7): if the caller fires two
 * `trackPage` events with the same `name` in quick succession,
 * both are enqueued. This matches the plan's "every page view"
 * claim in §3.1 and avoids a stateful last-emitted cache that
 * would have to be invalidated on history navigation.
 *
 * @param name The route name (e.g. `'InvoiceStatus'`,
 *   `'Login'`) or a logical screen identifier. Static literal
 *   preferred.
 */
function trackPage(name: string): void {
  track('page:' + name, { action: 'view' });
}

/**
 * Ship the in-memory queue to SynergyApi and clear it on
 * success. On any failure (network error, 5xx, 429), the
 * batch is prepended back to the queue and the backoff delay
 * is doubled (capped at `MAX_RETRY_DELAY`); on the first
 * subsequent success, the delay is reset to
 * `BASE_FLUSH_INTERVAL`.
 *
 * The function is `async` for the convenience of `await`-ing
 * the POST in tests, but public callers (`setInterval`,
 * `beforeunload`) treat it as fire-and-forget and do not await.
 *
 * HOTFIX 2026-06-10 (CORS preflight compatibility, AB#4259 follow-up #2):
 * The `X-Request-Id` header (F-8) is NO LONGER sent. Previously this
 * header was set on every POST via `window.generateUUID('usage-')`
 * (the helper remains in `GlobalVariables.ts:103-115` for the
 * `CustomModal.vue` consumer). Founder directive 2026-06-10 ruled the
 * FE-side fix over routing to SynergyApi for a CORS allowlist update:
 * the `X-` custom header triggers a browser preflight, and the
 * `Access-Control-Allow-Headers` echo on SynergyApi does not whitelist
 * it. Dropping the header brings the POST back to a "simple" request
 * (only `Content-Type: application/json`, which is non-safelisted but
 * accepted by the server). Trade-off: we lose the per-batch correlation
 * hook between a client session and the server's batch ingest. The
 * `window.generateUUID` helper is preserved for `CustomModal.vue`. A
 * deferred follow-up is to add `https://yango-backoffice.pages.dev` to
 * the SynergyApi CORS `Access-Control-Allow-Headers` allowlist and
 * restore `X-Request-Id` — tracked outside this hotfix.
 */
async function flush(): Promise<void> {
  if (queue.length === 0) {
    return;
  }
  // Snapshot the queue and clear the live reference BEFORE the
  // POST. This guarantees that a `track()` call during the
  // in-flight POST enqueues onto the (now empty) live queue
  // rather than being lost in the in-flight batch. On a
  // failure, the snapshot is prepended back so retry ordering
  // is preserved.
  const batch = queue.slice();
  queue = [];
  try {
    const response = await fetch(`${API_BASE}/record/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(batch),
    });
    if (!response.ok) {
      throw { status: response.status };
    }
    // Success: reset the backoff delay to the base. The
    // interval itself does not need to be torn down — it is
    // already firing at the right cadence.
    currentRetryDelay = BASE_FLUSH_INTERVAL;
  } catch (err: unknown) {
    // Failure: prepend the batch back so retry ordering is
    // preserved, then double the delay (F-4). The interval
    // MUST be torn down and re-registered at the new delay;
    // otherwise the OLD interval would keep firing at the OLD
    // delay until the new tick. This is the subtle Libertas
    // bug that this port fixes.
    queue = batch.concat(queue);
    if (currentRetryDelay < MAX_RETRY_DELAY) {
      currentRetryDelay = Math.min(currentRetryDelay * 2, MAX_RETRY_DELAY);
    }
    if (flushIntervalId !== null) {
      clearInterval(flushIntervalId);
      flushIntervalId = setInterval((): void => {
        void flush();
      }, currentRetryDelay);
    }
    // Diagnostic only — the tracker is fire-and-forget and
    // does not surface flush failures to the user.
    const status = err && typeof err === 'object' && 'status' in err
      ? (err as { status: number }).status
      : undefined;
    console.warn('[usageTracker] flush failed', { status, queued: queue.length });
  }
}

/**
 * Boot the tracker. Registers the 30-second flush interval and
 * the `beforeunload` best-effort final flush. Idempotent: a
 * second call is a silent no-op (F-5). The kill switch
 * `VITE_USAGE_TRACKING_ENABLED` is honoured here: when the
 * env var is set to a falsy value (`'0'`, `'false'`, `''`),
 * the tracker is a no-op for the lifetime of the page.
 *
 * Boot ordering: callers should invoke this AFTER the router
 * is ready, so the first `afterEach` page-view event is
 * captured in the queue. `main.ts:80-82` chains the call off
 * `router.isReady()` for exactly this reason.
 */
function init(): void {
  if (initialised) {
    return;
  }
  // Kill switch (gap analysis §6.3, AR-4 in the Pass A review).
  // Default-on; the only way to disable the tracker in a given
  // build is to set the env var to a falsy value at build time.
  // The check uses `import.meta.env`, which Vite inlines into
  // a static string at build time — the unset case resolves to
  // `undefined`, which is truthy-by-default.
  const enabledRaw = (import.meta as unknown as { env: Record<string, string | undefined> }).env
    .VITE_USAGE_TRACKING_ENABLED;
  const enabled = enabledRaw === undefined ? true : !['0', 'false', 'FALSE', ''].includes(enabledRaw);
  if (!enabled) {
    console.info('[usageTracker] disabled by VITE_USAGE_TRACKING_ENABLED');
    initialised = true;
    return;
  }
  initialised = true;
  // The interval callback is wrapped in an IIFE that discards
  // the returned promise. `flush()` is async for testability;
  // production callers treat it as fire-and-forget.
  flushIntervalId = setInterval((): void => {
    void flush();
  }, BASE_FLUSH_INTERVAL);
  // `beforeunload` is best-effort (R-4). We do not block unload
  // on the fetch promise — modern browsers do not honour that
  // pattern anyway. We just kick off one final flush and let
  // the browser tear down whatever is in flight. The event
  // listeners are NOT removed on `beforeunload` because the
  // page is being unloaded; the F-5 guard handles the HMR
  // case where `init()` is called twice in the same session.
  window.addEventListener('beforeunload', (): void => {
    void flush();
  });
}

/**
 * Public surface of the tracker. Exposed as a named export and
 * re-exposed on `window.usageTracker` by `GlobalVariables.ts:36-37`.
 * The shape is locked by the consumers in `router/index.ts`,
 * `main.ts`, and the `.vue` SFCs (LogIn.vue, Otp.vue,
 * InvoiceStatus.vue, etc.) — adding a method is a
 * consumer-visible change.
 */
export const usageTracker = {
  init,
  track,
  trackPage,
  flush,
};
